import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Typography } from '@mui/material';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import GenericDialog from 'portal/src/components/common/GenericDialog';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import {
  datePickerType,
  numberType,
  textInputType,
} from 'portal/src/components/common/forms/FormFieldTypes';

import { IHistoricalValueFormData } from './HistoricalValues.fields';

interface ComponentProps<T> {
  defaultValues: IHistoricalValueFormData<T>;
  formFieldsConfig: Array<datePickerType | numberType | textInputType>;
  onClose: () => void;
  onSave: (data: IHistoricalValueFormData<T>) => void;
  open: boolean;
  maxDate: Date;
}

function HistoricalValueForm<T>({
  defaultValues,
  formFieldsConfig,
  onClose,
  onSave,
  open,
  maxDate,
}: ComponentProps<T>) {
  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = useForm({ defaultValues });

  useEffect(() => {
    if (open) {
      reset({ ...defaultValues });
    }
  }, [defaultValues, open, reset]);

  const submitForm = (data: IHistoricalValueFormData<T>) => {
    onSave(data);
    reset({ ...defaultValues });
  };

  const handleClose = () => {
    onClose();
    reset({ ...defaultValues });
  };

  return (
    <GenericDialog open={open} onClose={handleClose}>
      <FormContainer
        header="Add Historical Value"
        backButtonlabel="Cancel"
        buttonLayout="bottom"
        control={control}
        isDirty={isDirty}
        isNew={true}
        isPending={false}
        isSubmitDisabled={false}
        mainFieldsConfig={formFieldsConfig}
        beforeMainFields={
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            variant="body2"
          >
            <KeyboardArrowRightIcon sx={{ width: 15, marginRight: 1 }} />
            Only entries equal to or before {format(maxDate, 'P')} can be added.
          </Typography>
        }
        handleBackAction={handleClose}
        handleSubmit={handleSubmit(submitForm)}
        removeContainerStyle={true}
      />
    </GenericDialog>
  );
}

export default HistoricalValueForm;
